import { reactive } from 'vue';
import { getValue } from '@lib/type-helpers.ts';
import type { HierarchyIconName } from './hierarchy-icons-data.ts';
// The generic glyph, statically: it is what every unnamed and not-yet-loaded node draws, so it has to
// be there from the first paint. One file, so the rest of the set can still arrive as its own chunk.
import genericIcon from '@icons/hierarchy-nodes/other.svg';

export type { HierarchyIconName };

export const hierarchyIcons: Record<string, string> = reactive({});

let loadPromise: Promise<void> | null = null;

/**
 * The set is a few hundred inline SVGs, so it is fetched rather than bundled into the entry chunk.
 *
 * A failed fetch retries by itself, with backoff. Retrying only on the next ask was not enough:
 * the askers are computeds, and a computed that already answered with the fallback has no reason
 * to ask again — nothing reactive changed when the fetch failed — so one flaky request (a deploy
 * mid-session, a network switch) left every hierarchy glyph generic until the tab was reloaded.
 * The reactive record is what the fallbacks read, so the retry that finally lands repaints them.
 */
let retries = 0;

export function loadHierarchyIcons(): Promise<void> {
  if (!loadPromise) {
    loadPromise = import('./hierarchy-icons-data.ts')
      .then((module) => {
        Object.assign(hierarchyIcons, module.hierarchyIcons);
      })
      .catch((error) => {
        loadPromise = null;
        // Never gives up — a network outage longer than any fixed number of tries would otherwise
        // leave every glyph generic until a reload — but backs off to a slow poll.
        setTimeout(() => void loadHierarchyIcons().catch(() => {}), Math.min(2 ** retries++, 30) * 1000);
        throw error;
      });
  }
  return loadPromise;
}

export function hierarchyIcon(name: HierarchyIconName): string;
export function hierarchyIcon(name: string | undefined): string;
export function hierarchyIcon(name: string | undefined): string {
  // Asking for an icon is what starts the fetch, so a caller that beat the store's own call still
  // gets the set — and gets the generic glyph to draw until it lands.
  void loadHierarchyIcons().catch(() => {});
  return getValue(hierarchyIcons, name ?? 'other') ?? hierarchyIcons.other ?? genericIcon;
}
